import cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'

import * as constants from '../constants/constants'

const getToken = () => cookies.get(constants.REACT_APP_AUTHENTICATE_TOKEN_NAME)

const deleteToken = () => {
    cookies.remove(constants.REACT_APP_AUTHENTICATE_TOKEN_NAME, {
        path: '/',
        domain: constants.REACT_APP_DOMAIN
    })
    cookies.remove('USER_ID', {
        path: '/',
        domain: constants.REACT_APP_DOMAIN
    })
    cookies.remove('USER_FIRST_NAME', {
        path: '/',
        domain: constants.REACT_APP_DOMAIN
    })
    cookies.remove('USER_LAST_NAME', {
        path: '/',
        domain: constants.REACT_APP_DOMAIN
    })
}

const readToken = (token) => {
    try {
        return jwtDecode(token)
    } catch (err) {
        deleteToken()
        window.location.replace('/login')
    }
}

const setToken = (token) => {
    cookies.set(constants.REACT_APP_AUTHENTICATE_TOKEN_NAME, token, {
        path: '/',
        domain: constants.REACT_APP_DOMAIN,
        expires: 2 * 365 * 24 * 60 * 60
    })
    syncUserProfileFromToken(token)
}

const setUserId = (token) => {
    cookies.set("USER_ID", token, {
        path: '/',
        domain: constants.REACT_APP_DOMAIN,
        expires: 2 * 365 * 24 * 60 * 60
    })
}

const setUserProfile = ({ firstName, lastName }) => {
    if (firstName) {
        cookies.set('USER_FIRST_NAME', firstName, {
            path: '/',
            domain: constants.REACT_APP_DOMAIN,
            expires: 2 * 365 * 24 * 60 * 60
        })
    }

    if (lastName) {
        cookies.set('USER_LAST_NAME', lastName, {
            path: '/',
            domain: constants.REACT_APP_DOMAIN,
            expires: 2 * 365 * 24 * 60 * 60
        })
    }
}

const extractProfileFields = (source = {}) => ({
    firstName:
        source.firstName ||
        source.first_name ||
        source.givenName ||
        source.given_name ||
        '',
    lastName:
        source.lastName ||
        source.last_name ||
        source.familyName ||
        source.family_name ||
        '',
})

const syncUserProfileFromToken = (token) => {
    if (!token) {
        return
    }

    try {
        const { firstName, lastName } = extractProfileFields(jwtDecode(token))

        if (firstName || lastName) {
            setUserProfile({ firstName, lastName })
        }
    } catch (error) {
        console.error('Error syncing user profile from token:', error)
    }
}

const getFirstName = (decoded) => {
    const fromToken = extractProfileFields(decoded).firstName
    return fromToken || cookies.get('USER_FIRST_NAME') || ''
}

const getLastName = (decoded) => {
    const fromToken = extractProfileFields(decoded).lastName
    return fromToken || cookies.get('USER_LAST_NAME') || ''
}

const formatFullName = (firstName, lastName) => {
    if (firstName && lastName) {
        return `${firstName} ${lastName}`
    }

    return firstName || lastName || ''
}

const getUserDetails = () => {
    try {
        const decoded = jwtDecode(getToken())
        return {
            userRoles: decoded?.Roles, userId: decoded.uam_id
        }
    } catch (error) {
        console.error('Error decoding token:', error)
        return {}
    }
}

const readUserId = () => cookies.get("USER_ID")

const isLoggedIn = () => Boolean(getToken())

const getUserId = () => {
    try {
        return readToken(getToken())?.uam_id
    } catch {
        return undefined
    }
}

const getUploaderUserId = () => readUserId() || getUserId() || null

const getDisplayName = () => {
    try {
        const token = getToken()
        if (!token) {
            return 'User'
        }

        if (!cookies.get('USER_FIRST_NAME') && !cookies.get('USER_LAST_NAME')) {
            syncUserProfileFromToken(token)
        }

        const decoded = jwtDecode(token)
        const firstName = getFirstName(decoded)
        const lastName = getLastName(decoded)
        const fullName = formatFullName(firstName, lastName)

        if (fullName) {
            return fullName
        }

        const directName =
            decoded?.name ||
            decoded?.user_name ||
            decoded?.userName ||
            decoded?.full_name ||
            decoded?.fullName

        if (directName && !String(directName).startsWith('CRN')) {
            return String(directName)
        }

        if (decoded?.admission_number) {
            return String(decoded.admission_number)
        }

        if (decoded?.phone) {
            return String(decoded.phone)
        }

        if (decoded?.email) {
            return String(decoded.email).split('@')[0]
        }

        return 'User'
    } catch (error) {
        console.error('Error reading display name:', error)
        return 'User'
    }
}

const getUserInitials = () => {
    try {
        const token = getToken()
        if (!token) {
            return 'U'
        }

        const decoded = jwtDecode(token)
        const firstName = getFirstName(decoded)
        const lastName = getLastName(decoded)

        if (firstName && lastName) {
            return `${firstName[0]}${lastName[0]}`.toUpperCase()
        }

        if (firstName) {
            return firstName.slice(0, 2).toUpperCase()
        }

        const displayName = getDisplayName()

        if (displayName === 'User') {
            return 'U'
        }

        const parts = displayName.trim().split(/\s+/).filter(Boolean)

        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        }

        return displayName.slice(0, 2).toUpperCase()
    } catch {
        return 'U'
    }
}

const isRoleValid = (tokenInfo) => {
    if (!tokenInfo || !tokenInfo.roles) {
        return false
    }
    return true
}

const isValidUser = () => {
    if (!isLoggedIn()) {
        return false;
    }

    const token = getToken();
    const tokenInfo = readToken(token);

    if (!tokenInfo) {
        return false;
    }
    return true;
};


export default {
    getToken,
    setToken,
    deleteToken,
    getUserId,
    getUploaderUserId,
    getDisplayName,
    getUserInitials,
    syncUserProfileFromToken,
    extractProfileFields,
    isValidUser,
    setUserId,
    setUserProfile,
    readUserId,
    getUserDetails
}
